/**
 * Counts how many copies of each character are currently in play by listening
 * to the token observer's "character-add" and "character-remove" events.
 * Construct it before registering your own handlers for those events so that
 * the counts are already updated when they run.
 */
export default class InPlayCounter {

    /**
     * @param {Observer} observer
     *        The observer that triggers the "character-add" and
     *        "character-remove" events.
     */
    constructor(observer) {

        /**
         * The counts for each character ID.
         * @type {Object}
         */
        this.counts = Object.create(null);

        observer.on("character-add", ({ detail }) => {

            const id = detail.character.getId();
            this.counts[id] = (this.counts[id] || 0) + 1;

        });

        observer.on("character-remove", ({ detail }) => {

            const id = detail.character.getId();

            if (this.counts[id]) {
                this.counts[id] -= 1;
            }

        });

    }

    /**
     * Gets the number of copies of the character that are in play.
     *
     * @param  {String} id
     *         ID of the character to check.
     * @return {Number}
     *         How many copies are in play.
     */
    count(id) {
        return this.counts[id] || 0;
    }

    /**
     * Checks to see if the character is in play.
     *
     * @param  {String} id
     *         ID of the character to check.
     * @return {Boolean}
     *         true if at least 1 copy is in play, false otherwise.
     */
    isInPlay(id) {
        return this.count(id) > 0;
    }

    /**
     * Gets the IDs of all the characters that are in play.
     *
     * @return {Array.<String>}
     *         The IDs of the characters in play.
     */
    idsInPlay() {

        return Object
            .keys(this.counts)
            .filter((id) => this.counts[id] > 0);

    }

}
